import React, { Fragment, useEffect } from 'react';
import { Formik, Form, Field } from 'formik';
import { Link } from 'react-router-dom';
import { TextField } from 'formik-material-ui';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router';
import { useTranslation } from 'react-i18next';

import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import IconButton from '@material-ui/core/IconButton';
import InputAdornment from '@material-ui/core/InputAdornment';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import Visibility from '@material-ui/icons/Visibility';
import VisibilityOff from '@material-ui/icons/VisibilityOff';

import Button from '../../../components/UI/inputs/Button';
import { setLoggedInUser, setActiveAccount } from '../../../store/globalSlice';
import { authDoctor } from '../../../services/auth.service';

import { getAuthValidationSchema } from '../../../utils/forms/validators/schemaValidators.yup';
import {
	capitalizeFirstLetter,
	getLocalStorageItem,
	setLocalStorageItem,
	setUserId,
	toastError,
	toastSuccess,
} from '../../../utils/globals.utils';
import {
	DASHBOARD_HOME,
	PARTNER_ROLE,
	RESPONSIVE_MOBILE_QUERY,
} from '../../../utils/constants';
import { useAuthStyles } from '../auth.style';

import Flag from '../../../assets/images/ukFlag.png';
import logo from '../../../assets/images/ctdent_logo.png';
import authImage from '../../../assets/images/authImage.jpg';
import { useMediaQuery } from 'react-responsive';

const SignIn = () => {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const classes = useAuthStyles();
	const history = useHistory();
	const isTabletOrMobile = useMediaQuery(RESPONSIVE_MOBILE_QUERY);

	const formInitialValues = {
		username: '',
		password: '',
	};

	const [loading, setLoading] = React.useState(false);
	const [showPassword, setShowPassword] = React.useState(false);

	const handleUserAuth = (res) => {
		console.log('res', res);
		setLoading(false);
		if (res.data.responseCode === 2014) {
			toastError(t('commonMessage.passwordIsWrong'));
		} else if (res.data.responseCode === 200) {
			dispatch(setLoggedInUser(res.data.responseData));
			dispatch(setActiveAccount(res.data.responseData.id));
			setLocalStorageItem('userData', JSON.stringify(res.data.responseData));
			setUserId(res.data.responseData.id);
			setLocalStorageItem('authToken', res.data.responseData.token);
			toastSuccess(
				`${capitalizeFirstLetter(
					res?.data?.responseData?.doctor_full_name,
				)} - ${t(
					res?.data?.responseData?.roles?.includes(PARTNER_ROLE)
						? 'commonMessage.loginSuccessfullyAsPartner'
						: 'commonMessage.loginSuccessfully',
				)}`,
			);
			history.push(DASHBOARD_HOME);
		} else if (res.data.responseMessage) {
			toastError(res.data.responseMessage);
		} else {
			toastError(t('commonMessage.somethingWentWrong'));
		}
	};

	// authenticate the doctor and redirect to dashboard.
	const onSubmitDoctorForm = (values, { setSubmitting }) => {
		setSubmitting(false);
		setLoading(true);
		authDoctor(values.username, values.password)
			.then((res) => {
				handleUserAuth(res);
			})
			.catch(() => setLoading(false));
	};

	useEffect(() => {
		async function verifyAuthentication() {
			const accessToken = await getLocalStorageItem('authToken');
			if (accessToken) history.push(DASHBOARD_HOME);
		}
		verifyAuthentication();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<Fragment>
			<Grid item xs={false} md={6} className={classes.image}>
				<img src={logo} alt="" className={classes.logo} />
				<img src={authImage} alt="" className={classes.aiImage} />
				<Box className={classes.info}>
					<Typography variant="h2" className={classes.heading}>
						Wise Dentists
					</Typography>
					<Typography variant="h2" className={classes.subHeading}>
						Choose AI
					</Typography>
				</Box>
			</Grid>
			<Grid
				container
				item
				xs={12}
				lg={6}
				component={Paper}
				elevation={6}
				square
				alignItems={isTabletOrMobile ? 'baseline' : 'center'}
				justifyContent="center"
				className={
					isTabletOrMobile
						? classes.mobileBackground
						: classes.desktopBackground
				}
			>
				{isTabletOrMobile && (
					<Grid
						container
						item
						xs={12}
						lg={6}
						component={Paper}
						elevation={6}
						square
						alignItems={isTabletOrMobile ? 'baseline' : 'center'}
						justifyContent="center"
						className={classes.mobileColorBackground}
					>
						<Box style={{ textAlign: 'center', width: '100%' }}>
							<img
								src={logo}
								alt=""
								className={classes.logo}
								style={{ paddingTop: '50px' }}
							/>
						</Box>
						<Paper className={classes.paper} style={{ marginTop: '40px' }}>
							<Grid
								container
								item
								alignItems="center"
								justifyContent="space-between"
								style={{
									marginBottom: isTabletOrMobile ? '20px' : '108px',
								}}
							>
								<Typography
									className={classes.loginHeading}
									style={{ fontSize: isTabletOrMobile && '24px' }}
								>
									<span>Log In</span> | Doctor Account
								</Typography>
								<img src={Flag} alt="" />
							</Grid>
							<Formik
								initialValues={formInitialValues}
								validationSchema={getAuthValidationSchema(
									t('forms.validators.required'),
								)}
								onSubmit={onSubmitDoctorForm}
							>
								{({ values, setFieldValue }) => (
									<Form className={classes.form}>
										<Typography className={classes.inputLabel} align="left">
											User Name
										</Typography>
										<Field
											type="email"
											variant="outlined"
											margin="normal"
											required
											fullWidth
											id="username"
											placeholder={t('forms.username')}
											name="username"
											autoComplete="username"
											autoFocus
											onChange={(event) => {
												if (event.target.value?.length > 60) return;
												setFieldValue('username', event.target.value);
											}}
											size="small"
											component={TextField}
										/>
										<Typography className={classes.inputLabel} align="left">
											Password
										</Typography>
										<Field
											variant="outlined"
											margin="normal"
											required
											fullWidth
											name="password"
											placeholder={t('forms.password')}
											type={showPassword ? 'text' : 'password'}
											id="password"
											autoComplete="current-password"
											size="small"
											shrink={false}
											component={TextField}
											onChange={(event) => {
												if (event.target.value?.length > 30) return;
												setFieldValue('password', event.target.value);
											}}
											onKeyPress={(event) => {
												console.log('event', event);
											}}
											InputProps={{
												endAdornment: (
													<InputAdornment position="end">
														<IconButton
															aria-label="toggle password visibility"
															onClick={() => setShowPassword((value) => !value)}
															edge="end"
														>
															{showPassword ? (
																<Visibility />
															) : (
																<VisibilityOff />
															)}
														</IconButton>
													</InputAdornment>
												),
											}}
										/>
										<Typography align="right">
											<Link
												to="/auth/forgot-password"
												className={classes.forgotPassword}
											>
												Forgot password?
											</Link>
										</Typography>

										<Button
											loading={loading}
											type="submit"
											className={classes.submit}
											label={t('auth.signIn')}
										/>
									</Form>
								)}
							</Formik>
							<Grid container alignItems="center" justifyContent="center">
								<Grid item>
									<Link
										to="/auth/register"
										variant="body2"
										className={classes.signUp}
									>
										{t('auth.dontHaveAccount')}
									</Link>
								</Grid>
							</Grid>
						</Paper>
					</Grid>
				)}
				{!isTabletOrMobile && (
					<Paper className={classes.paper}>
						<Grid
							container
							item
							alignItems="center"
							justifyContent="space-between"
							style={{
								marginBottom: isTabletOrMobile ? '20px' : '108px',
							}}
						>
							<Typography
								className={classes.loginHeading}
								style={{ fontSize: isTabletOrMobile && '24px' }}
							>
								<span>Log In</span> | Doctor Account
							</Typography>
							<img src={Flag} alt="" />
						</Grid>
						<Formik
							initialValues={formInitialValues}
							validationSchema={getAuthValidationSchema(
								t('forms.validators.required'),
							)}
							onSubmit={onSubmitDoctorForm}
						>
							{({ values, setFieldValue }) => (
								<Form className={classes.form}>
									<Typography className={classes.inputLabel} align="left">
										User Name
									</Typography>
									<Field
										type="email"
										variant="outlined"
										margin="normal"
										required
										fullWidth
										id="username"
										placeholder={t('forms.username')}
										name="username"
										autoComplete="username"
										autoFocus
										size="small"
										component={TextField}
										onChange={(event) => {
											if (event.target.value?.length > 60) return;
											setFieldValue('username', event.target.value);
										}}
									/>
									<Typography className={classes.inputLabel} align="left">
										Password
									</Typography>
									<Field
										variant="outlined"
										margin="normal"
										required
										fullWidth
										name="password"
										placeholder={t('forms.password')}
										type={showPassword ? 'text' : 'password'}
										id="password"
										autoComplete="current-password"
										size="small"
										shrink={false}
										component={TextField}
										onChange={(event) => {
											if (event.target.value?.length > 30) return;
											setFieldValue('password', event.target.value);
										}}
										InputProps={{
											endAdornment: (
												<InputAdornment position="end">
													<IconButton
														aria-label="toggle password visibility"
														onClick={() => setShowPassword((value) => !value)}
														edge="end"
													>
														{showPassword ? <Visibility /> : <VisibilityOff />}
													</IconButton>
												</InputAdornment>
											),
										}}
									/>
									<Typography align="right">
										<Link
											to="/auth/forgot-password"
											className={classes.forgotPassword}
										>
											Forgot password?
										</Link>
									</Typography>

									<Button
										loading={loading}
										type="submit"
										className={classes.submit}
										label={t('auth.signIn')}
									/>
								</Form>
							)}
						</Formik>
						<Grid container alignItems="center" justifyContent="center">
							<Grid item>
								<Link
									to="/auth/register"
									variant="body2"
									className={classes.signUp}
								>
									{t('auth.dontHaveAccount')}
								</Link>
							</Grid>
						</Grid>
					</Paper>
				)}
			</Grid>
		</Fragment>
	);
};

export default SignIn;
